import React, { useState, useEffect, useRef } from "react";
import "../../../../style/journalView.css";
import { Calendar, ChevronDown, Search } from "lucide-react";
import { useSelector } from "react-redux";

const JournalEditModal = ({
  isOpen,
  onClose,
  transaction,
  customers = [],
  onSave,
}) => {
  const [formData, setFormData] = useState({
    date: "",
    customerId: "",
    customer: "",
    credit: "",
    debit: "",
    note: "",
  });

  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const customerSearchInputRef = useRef(null);
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);

  // Initialize form data when transaction changes
  useEffect(() => {
    if (transaction) {
      // Get customer ID from originalData or find by name
      const customerId = transaction.originalData?.customer?.toString() || 
                        customers.find((c) => c.name === transaction.customer)?.id || 
                        "";

      setFormData({
        date: transaction.date || "",
        customerId: customerId,
        customer: transaction.customer || "",
        credit: transaction.credit ? transaction.credit.toString() : "",
        debit: transaction.debit ? transaction.debit.toString() : "",
        note: transaction.note || "",
      });
    }
  }, [transaction]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isCustomerDropdownOpen && customerSearchInputRef.current) {
      customerSearchInputRef.current.focus();
    }
  }, [isCustomerDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCustomerDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };
      
      // If credit is being filled, clear debit
      if (field === 'credit' && parseFloat(value) > 0) {
        newData.debit = '';
      }
      
      // If debit is being filled, clear credit
      if (field === 'debit' && parseFloat(value) > 0) {
        newData.credit = '';
      }
      
      return newData;
    });
  };

  // Check if credit field should be disabled
  const isCreditDisabled = formData.debit && parseFloat(formData.debit) > 0;
  
  // Check if debit field should be disabled
  const isDebitDisabled = formData.credit && parseFloat(formData.credit) > 0;

  const handleCustomerSelect = (customer) => {
    setFormData((prev) => ({
      ...prev,
      customerId: customer.id,
      customer: customer.name,
    }));
    setIsCustomerDropdownOpen(false);
    setCustomerSearchTerm("");
  };

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.customerId) {
      alert("Please select a customer");
      return;
    }

    if (!formData.debit && !formData.credit) {
      alert("Please enter either debit or credit amount");
      return;
    }

    if (formData.debit && formData.credit) {
      alert("Please enter either debit or credit amount, not both");
      return;
    }

    // Prepare updated transaction data
    const updatedTransaction = {
      ...transaction,
      customer: formData.customer,
      credit: parseFloat(formData.credit) || 0,
      debit: parseFloat(formData.debit) || 0,
      note: formData.note || "No note provided",
      // Note: date is not editable as per requirement
    };

    onSave(updatedTransaction);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen || !transaction) return null;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="journal-edit-modal no-scrollbar">
        <div className="journal-edit-modal-header">
          <div>
            <h2 className="journal-edit-modal-title">Edit Transaction</h2>
            <p className="journal-edit-modal-subtitle">
              Edit and update transaction details below
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="journal-edit-modal-form">
          <div className="journal-edit-form-row">
            <div className="journal-edit-form-group">
              <label htmlFor="date">Date *</label>
              <div
                className="journal-edit-date-input-wrapper"
                style={{ width: "100%" }}
              >
                <Calendar
                  className="journal-edit-calendar-icon-left"
                  size={16}
                />
                <input
                  type="text"
                  id="date"
                  value={formData.date}
                  readOnly
                  className={`${isPartsURL ? "journal-edit-form-input-parts date-readonly date-with-icon full-width" : isAccessoriesURL ? "journal-edit-form-input-acc date-readonly date-with-icon full-width" : "journal-edit-form-input date-readonly date-with-icon full-width"}`}
                  placeholder="19/09/2025"
                />
              </div>
            </div>
          </div>

          <div className="journal-edit-form-row">
            <div className="journal-edit-form-group">
              <label htmlFor="customer">Customer *</label>
              <div
                className="journal-customer-dropdown-container"
                ref={dropdownRef}
              >
                <div
                  className={`${isPartsURL ? "journal-dropdown-input-parts" : isAccessoriesURL ? "journal-dropdown-input-acc" : "journal-dropdown-input"}`}
                  style={{ width: "100%" }}
                  onClick={() =>
                    setIsCustomerDropdownOpen(!isCustomerDropdownOpen)
                  }
                >
                  <span
                    className={
                      formData.customer
                        ? "journal-selected-value"
                        : "journal-not-selected-value"
                    }
                  >
                    {formData.customerId + " " + formData.customer || "Select customer"}
                  </span>
                  <ChevronDown className="journal-dropdown-arrow" size={16} />
                </div>

                {isCustomerDropdownOpen && (
                  <div className="journal-dropdown-menu">
                    <div className="journal-dropdown-search">
                      <Search className="journal-search-icon" size={16} />
                      <input
                        ref={customerSearchInputRef}
                        type="text"
                        className={`${isPartsURL ? "journal-search-input-parts" : isAccessoriesURL ? "journal-search-input-acc" : "journal-search-input"} full-width`}
                        placeholder="Search customers..."
                        value={customerSearchTerm}
                        onChange={(e) => setCustomerSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="journal-dropdown-list noscrollbar">
                      {filteredCustomers.length > 0 ? (
                        filteredCustomers.map((customer) => (
                          <div
                            key={customer.id}
                            className="journal-dropdown-item"
                            onClick={() => handleCustomerSelect(customer)}
                          >
                            {customer.id + " " + customer.name}
                          </div>
                        ))
                      ) : (
                        <div className="journal-dropdown-item journal-no-results">
                          No customers found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="journal-edit-form-row">
            <div className="journal-edit-form-group">
              <label htmlFor="credit">Credit *</label>
              <input
                type="number"
                id="credit"
                value={formData.credit}
                onChange={(e) => handleInputChange("credit", e.target.value)}
                className={`${isPartsURL ? "journal-edit-form-input-parts" : isAccessoriesURL ? "journal-edit-form-input-acc" : "journal-edit-form-input"} ${isCreditDisabled ? 'disabled' : ''}`}
                placeholder="€0.00"
                step="0.01"
                min="0"
                disabled={isCreditDisabled}
              />
            </div>
          </div>

          <div className="journal-edit-form-row">
            <div className="journal-edit-form-group">
              <label htmlFor="debit">Debit *</label>
              <input
                type="number"
                id="debit"
                value={formData.debit}
                onChange={(e) => handleInputChange("debit", e.target.value)}
                className={`${isPartsURL ? "journal-edit-form-input-parts" : isAccessoriesURL ? "journal-edit-form-input-acc" : "journal-edit-form-input"} ${isDebitDisabled ? 'disabled' : ''}`}
                placeholder="€0.00"
                step="0.01"
                min="0"
                disabled={isDebitDisabled}
              />
            </div>
          </div>

          <div className="journal-edit-form-row">
            <div className="journal-edit-form-group">
              <label htmlFor="note">Note</label>
              <textarea
                id="note"
                value={formData.note}
                style={{ height: "60px" }}
                onChange={(e) => handleInputChange("note", e.target.value)}
                className={`${isPartsURL ? "journal-edit-form-textarea-parts" : isAccessoriesURL ? "journal-edit-form-textarea-acc" : "journal-edit-form-textarea"}`}
                placeholder="Enter note..."
                rows={3}
              />
            </div>
          </div>

          <div className="journal-edit-modal-actions">
            <button
              type="button"
              className="journal-edit-cancel-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button type="submit" className={`${isPartsURL ? "journal-edit-update-btn-parts" : isAccessoriesURL ? "journal-edit-update-btn-acc" : "journal-edit-update-btn"}`}>
              Update Transaction
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JournalEditModal;
